import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminJobs() {
  const { t, getLocalizedContent } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title: { fr: '', en: '' },
    description: { fr: '', en: '' },
    requirements: { fr: '', en: '' },
    location: 'Yaoundé',
    contract_type: 'CDI',
    department: '',
    deadline: '',
    is_active: true
  });

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/jobs?active_only=false`);
      setItems(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/jobs/${editingItem.id}`, formData);
        toast.success('Offre mise à jour');
      } else {
        await axios.post(`${API}/admin/jobs`, formData);
        toast.success('Offre créée');
      }
      setShowModal(false);
      resetForm();
      fetchItems();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ?')) return;
    try {
      await axios.delete(`${API}/admin/jobs/${id}`);
      toast.success('Supprimé');
      fetchItems();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title || { fr: '', en: '' },
      description: item.description || { fr: '', en: '' },
      requirements: item.requirements || { fr: '', en: '' },
      location: item.location || 'Yaoundé',
      contract_type: item.contract_type || 'CDI',
      department: item.department || '',
      deadline: item.deadline || '',
      is_active: item.is_active !== false
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      title: { fr: '', en: '' },
      description: { fr: '', en: '' },
      requirements: { fr: '', en: '' },
      location: 'Yaoundé',
      contract_type: 'CDI',
      department: '',
      deadline: '',
      is_active: true
    });
  };

  return (
    <div data-testid="admin-jobs">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#0F172A] font-['Montserrat']">{t('admin_jobs')}</h1>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-[#0B6AB3] hover:bg-[#095c9e] text-white">
          <Plus className="h-4 w-4 mr-2" />{t('admin_new')}
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B6AB3]"></div></div>
      ) : (
        <div className="space-y-4">
          {items.map((item) => (
            <Card key={item.id} className="border-0 shadow-sm">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-[#0F172A]">{getLocalizedContent(item.title)}</h3>
                    <Badge variant="outline">{item.contract_type}</Badge>
                  </div>
                  <p className="text-sm text-gray-500">{item.location} • {item.department}</p>
                  {item.deadline && <p className="text-xs text-gray-400 mt-1">Date limite: {new Date(item.deadline).toLocaleDateString('fr-FR')}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs rounded-full ${item.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                    {item.is_active ? 'Actif' : 'Inactif'}
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(item)}><Edit className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingItem ? 'Modifier' : 'Nouvelle offre'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Titre (FR) *</Label><Input value={formData.title.fr} onChange={(e) => setFormData({ ...formData, title: { ...formData.title, fr: e.target.value } })} required /></div>
              <div><Label>Titre (EN)</Label><Input value={formData.title.en} onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })} /></div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Description (FR) *</Label><Textarea value={formData.description.fr} onChange={(e) => setFormData({ ...formData, description: { ...formData.description, fr: e.target.value } })} rows={3} required /></div>
              <div><Label>Description (EN)</Label><Textarea value={formData.description.en} onChange={(e) => setFormData({ ...formData, description: { ...formData.description, en: e.target.value } })} rows={3} /></div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Prérequis (FR)</Label><Textarea value={formData.requirements.fr} onChange={(e) => setFormData({ ...formData, requirements: { ...formData.requirements, fr: e.target.value } })} rows={3} /></div>
              <div><Label>Prérequis (EN)</Label><Textarea value={formData.requirements.en} onChange={(e) => setFormData({ ...formData, requirements: { ...formData.requirements, en: e.target.value } })} rows={3} /></div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div><Label>Lieu</Label><Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} /></div>
              <div>
                <Label>Type de contrat</Label>
                <Select value={formData.contract_type} onValueChange={(v) => setFormData({ ...formData, contract_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CDI">CDI</SelectItem>
                    <SelectItem value="CDD">CDD</SelectItem>
                    <SelectItem value="Stage">Stage</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Département</Label><Input value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} /></div>
            </div>
            <div><Label>Date limite</Label><Input type="date" value={formData.deadline} onChange={(e) => setFormData({ ...formData, deadline: e.target.value })} /></div>
            <div className="flex items-center gap-2"><Switch checked={formData.is_active} onCheckedChange={(v) => setFormData({ ...formData, is_active: v })} /><Label>Offre active</Label></div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>{t('common_cancel')}</Button>
              <Button type="submit" className="bg-[#0B6AB3] hover:bg-[#095c9e] text-white">{t('common_save')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
